import { useMemo, useState, type CSSProperties } from "react";
import {
  ArrowDown,
  ArrowUp,
  CaretLeft,
  CaretRight,
  Cube,
  Eye,
  EyeSlash,
  Info,
  Stack,
  LockSimple,
  LockSimpleOpen,
  MagnifyingGlass,
  MapPin,
  Package,
  Plus,
  Selection,
  Trash,
  TreeStructure,
  Warning,
} from "@phosphor-icons/react";
import { getAssetDefinition } from "../domain/assets";
import { missingStorageCount } from "../domain/storage-templates";
import { LABORATORY_MATERIAL_TEXTURES } from "../lib/laboratory-material-textures";
import { validatePlacement } from "../domain/geometry";
import { getLocationPath, indexingStats } from "../domain/indexing";
import { LABORATORY_ENVIRONMENT_PROFILES } from "../domain/laboratory-environment";
import {
  findLaboratoryFloorFinish,
  LABORATORY_FLOOR_FINISHES,
  laboratoryFloorFinishLabel,
} from "../domain/laboratory-materials";
import {
  findLaboratoryWallFinish,
  LABORATORY_WALL_FINISHES,
  wallFinishForObject,
} from "../domain/laboratory-wall-materials";
import { normalizeRaisedFromFloorMm } from "../domain/object-transforms";
import { getClosedWallFloorPolygon, getRoomSpaceFloorPlans } from "../domain/room-geometry";
import type { EquipmentRecord, Room, SceneObject } from "../domain/schema";
import { selectActiveRoom, useEditorStore, type InspectorPanel } from "../store/editor-store";
import { planAnnex, stageAnnexPlan } from "../agent/labspace-annex-actions";
import { InventoryOrganizer, type InventoryOrganizerOptions } from "./InventoryOrganizer";
import { AssetThumbnail } from "./AssetThumbnail";
import { StorageInspector } from "./StorageInspector";

const panelTabs: Array<{ id: InspectorPanel; label: string; icon: typeof Info }> = [
  { id: "room", label: "Room", icon: Info },
  { id: "layers", label: "Layers", icon: Stack },
  { id: "index", label: "Storage", icon: TreeStructure },
  { id: "inventory", label: "Inventory", icon: Package },
  { id: "properties", label: "Details", icon: Selection },
  { id: "validation", label: "Issues", icon: Warning },
];

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="info-stat">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  suffix,
  multiline = false,
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
  suffix?: string;
  multiline?: boolean;
}) {
  return (
    <label className="property-field">
      <span>{label}</span>
      <div className="input-with-suffix">
        {multiline ? (
          <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={3} />
        ) : (
          <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
        )}
        {suffix && <em>{suffix}</em>}
      </div>
    </label>
  );
}

type SurfaceMaterialChoice = {
  id: string;
  label: string;
  description: string;
  color: string;
  accent: string;
  pattern?: string;
  textureKind?: keyof typeof LABORATORY_MATERIAL_TEXTURES;
};

function SurfaceMaterialPicker({
  label,
  value,
  choices,
  onChange,
  compact = false,
}: {
  label: string;
  value: string;
  choices: readonly SurfaceMaterialChoice[];
  onChange: (value: string) => void;
  compact?: boolean;
}) {
  const selectedChoice = choices.find((choice) => choice.id === value);
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  return (
    <div className={`surface-material-group finish-chooser ${compact ? "is-compact" : ""}`}>
      <button
        className="finish-current"
        aria-label={`Change ${label.toLowerCase()}`}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <i
          style={{
            backgroundColor: selectedChoice?.color,
            backgroundImage: selectedChoice?.textureKind
              ? `url(${LABORATORY_MATERIAL_TEXTURES[selectedChoice.textureKind].url})`
              : undefined,
          }}
        />
        <span>
          <small>{label}</small>
          <b>{selectedChoice?.label ?? "Custom finish"}</b>
          <em>{expanded ? "Close finish library" : "Choose another finish"}</em>
        </span>
        <CaretRight size={16} />
      </button>
      {expanded && (
        <div className="finish-library">
          <input
            aria-label={`Search ${label.toLowerCase()} materials`}
            placeholder="Find a finish…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div
            className="surface-material-grid"
            role="list"
            aria-label={`${label} material library`}
          >
            {choices
              .filter((choice) =>
                `${choice.label} ${choice.description}`
                  .toLowerCase()
                  .includes(search.toLowerCase()),
              )
              .map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  className={`surface-material-card ${choice.id === value ? "active" : ""}`}
                  aria-pressed={choice.id === value}
                  onClick={() => {
                    onChange(choice.id);
                    setExpanded(false);
                  }}
                  title={choice.description}
                >
                  <i
                    className={choice.pattern ? `pattern-${choice.pattern}` : ""}
                    style={
                      {
                        "--surface-color": choice.color,
                        "--surface-accent": choice.accent,
                        ...(choice.textureKind
                          ? {
                              backgroundImage: `url(${LABORATORY_MATERIAL_TEXTURES[choice.textureKind].url})`,
                              backgroundSize: "cover",
                              backgroundBlendMode: "multiply",
                              backgroundColor: choice.color,
                            }
                          : {}),
                      } as CSSProperties
                    }
                  />
                  <span>
                    <b>{choice.label}</b>
                    {!compact && <small>{choice.description}</small>}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnnexBuilder({ room }: { room: Room }) {
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const saveStatus = useEditorStore((state) => state.saveStatus);
  const pushToast = useEditorStore((state) => state.pushToast);
  const updateRoom = useEditorStore((state) => state.updateRoom);
  const walls = room.scene.objects.filter(
    (object) => object.wall && !object.wall.halfHeight && object.visible,
  );
  const selectedWall = walls.find((wall) => selectedIds.includes(wall.id));
  const [hostWallId, setHostWallId] = useState(walls[0]?.id ?? "");
  const [name, setName] = useState("Preparation annex");
  const [code, setCode] = useState(`${room.code}-A${room.spaces.length}`);
  const [widthM, setWidthM] = useState(4.5);
  const [depthM, setDepthM] = useState(3.6);
  const [connector, setConnector] = useState(true);
  const [windowCount, setWindowCount] = useState(1);
  const effectiveWallId = selectedWall?.id ?? hostWallId ?? walls[0]?.id ?? "";
  const selectedHost = walls.find((wall) => wall.id === effectiveWallId);
  const spaces = getRoomSpaceFloorPlans(room);

  const preview = () => {
    if (!selectedHost?.wall) {
      pushToast("Select a full-height room wall before adding an annex.", "error");
      return;
    }
    if (saveStatus !== "saved") {
      pushToast("Finish saving current edits before previewing an annex.", "info");
      return;
    }
    try {
      const widthAlongWallMm = Math.round(widthM * 1000);
      const outwardDepthMm = Math.round(depthM * 1000);
      const windowOffsets =
        windowCount === 2
          ? [widthAlongWallMm / 3, (widthAlongWallMm * 2) / 3]
          : windowCount === 1
            ? [widthAlongWallMm / 2]
            : [];
      const plan = planAnnex({
        parentRoomCode: room.code,
        name,
        code,
        hostWallId: selectedHost.id,
        widthAlongWallMm,
        outwardDepthMm,
        floorFinish: room.floorFinish,
        connector: connector
          ? {
              assetId: "narrow-lite-door",
              offsetMm: widthAlongWallMm / 2,
              handing: "left",
              opensInto: "annex",
            }
          : undefined,
        windows: windowOffsets.map((offsetMm) => ({
          assetId: "standard-window",
          wall: "outer",
          offsetMm,
          sillHeightMm: 900,
        })),
        assets: [],
      });
      stageAnnexPlan({ planId: plan.planId });
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "The annex could not be planned.", "error");
    }
  };

  return (
    <details className="inspector-section inspector-card inspector-disclosure annex-builder">
      <summary>
        <span>
          <h3>Spaces &amp; annexes</h3>
          <small>{spaces.length} independent floor{spaces.length === 1 ? "" : "s"}</small>
        </span>
        <CaretRight size={16} />
      </summary>
      <div className="annex-space-ledger" aria-label="Room spaces">
        {spaces.map((space) => (
          <span key={space.spaceId}>
            <i>{space.kind === "primary" ? "P" : "A"}</i>
            <b>{space.name}</b>
            <small>
              {space.code} · {(space.areaMm2 / 1_000_000).toFixed(2)} m²
            </small>
            <select
              value={space.floorFinish}
              onChange={(event) => {
                const floorFinish = event.target.value;
                updateRoom({
                  ...(space.kind === "primary" ? { floorFinish } : {}),
                  spaces: room.spaces.map((entry) =>
                    entry.id === space.spaceId ? { ...entry, floorFinish } : entry,
                  ),
                });
              }}
              aria-label={`${space.name} floor finish`}
            >
              {LABORATORY_FLOOR_FINISHES.map((finish) => (
                <option value={finish.id} key={finish.id}>
                  {finish.label}
                </option>
              ))}
            </select>
          </span>
        ))}
      </div>
      <div className="annex-builder-heading">
        <span>
          <small className="eyebrow">Add connected space</small>
          <b>Build from one existing wall</b>
        </span>
        <em>Always reviewed</em>
      </div>
      <label className="property-field annex-wall-field">
        <span>Host wall</span>
        <div className="input-with-suffix">
          <select
            value={effectiveWallId}
            onChange={(event) => setHostWallId(event.target.value)}
            aria-label="Annex host wall"
          >
            {walls.map((wall, index) => (
              <option value={wall.id} key={wall.id}>
                Wall {index + 1} · {(wall.dimensions.width / 1000).toFixed(2)} m
                {selectedWall?.id === wall.id ? " · selected" : ""}
              </option>
            ))}
          </select>
        </div>
      </label>
      <div className="property-grid two annex-identity-fields">
        <TextField label="Annex name" value={name} onChange={setName} />
        <TextField label="Space code" value={code} onChange={(value) => setCode(value.toUpperCase())} />
        <TextField
          label="Width on wall"
          type="number"
          suffix="m"
          value={widthM}
          onChange={(value) => setWidthM(Number(value))}
        />
        <TextField
          label="Outward depth"
          type="number"
          suffix="m"
          value={depthM}
          onChange={(value) => setDepthM(Number(value))}
        />
      </div>
      <div className="annex-options" aria-label="Annex openings">
        <label>
          <input
            type="checkbox"
            checked={connector}
            onChange={(event) => setConnector(event.target.checked)}
          />
          <span>
            <b>Internal door</b>
            <small>Narrow-lite door opening into annex</small>
          </span>
        </label>
        <label>
          <span>
            <b>Outer windows</b>
            <small>Evenly distributed observation glazing</small>
          </span>
          <select
            value={windowCount}
            onChange={(event) => setWindowCount(Number(event.target.value))}
            aria-label="Annex outer windows"
          >
            <option value={0}>None</option>
            <option value={1}>1 window</option>
            <option value={2}>2 windows</option>
          </select>
        </label>
      </div>
      <button className="button-primary annex-preview-button" onClick={preview} disabled={!walls.length}>
        <Plus size={16} weight="bold" /> Preview connected annex
      </button>
      <p className="annex-builder-note">
        LabSpace preserves the primary floor, remaps hosted openings, and commits the annex as one
        undoable review.
      </p>
    </details>
  );
}

function RoomPanel() {
  const room = useEditorStore(selectActiveRoom);
  const updateRoom = useEditorStore((state) => state.updateRoom);
  const stats = indexingStats(room.scene);
  const closedFloor = getClosedWallFloorPolygon(room.scene.objects);
  const spaceFloors = getRoomSpaceFloorPlans(room);
  const totalFloorAreaMm2 = spaceFloors.reduce((total, floor) => total + floor.areaMm2, 0);
  const registeredFloorFinish = findLaboratoryFloorFinish(room.floorFinish);
  const registeredWallFinish = findLaboratoryWallFinish(room.wallFinish);
  const [surfaceTarget, setSurfaceTarget] = useState<"floor" | "walls">("floor");
  const floorChoices = LABORATORY_FLOOR_FINISHES.map((finish) => ({
    id: finish.id,
    label: finish.label,
    description: finish.description,
    color: finish.planColor,
    accent: finish.patternColor,
    pattern: finish.pattern,
    textureKind: finish.textureKind,
  }));
  const wallChoices = LABORATORY_WALL_FINISHES.map((finish) => ({
    id: finish.id,
    label: finish.label,
    description: finish.description,
    color: finish.color,
    accent: finish.baseboardColor,
    textureKind: finish.textureKind,
  }));
  return (
    <div className="inspector-scroll inspector-studio-panel room-inspector-studio">
      <section className="inspector-section room-summary inspector-dossier room-dossier">
        <header>
          <div className="room-code-mark">
            <Cube size={28} weight="duotone" />
            <span>
              <small>Active room</small>
              <b>Room · {room.name}</b>
              <em>{room.code}</em>
            </span>
          </div>
          <span className="dossier-status">
            <i /> {stats.totalLocations} indexed locations
          </span>
        </header>
        <p>{laboratoryFloorFinishLabel(room.floorFinish)} floor · editable spatial workspace</p>
        <div className="stat-grid">
          <Stat
            label="Total floor"
            value={`${(totalFloorAreaMm2 / 1_000_000).toFixed(2)} m²`}
          />
          <Stat
            label="Perimeter"
            value={`${((closedFloor?.perimeterMm ?? 0) / 1000).toFixed(2)} m`}
          />
          <Stat label="Wall height" value={`${(room.wallHeight / 1000).toFixed(2)} m`} />
          <Stat label="Spaces" value={spaceFloors.length} />
        </div>
      </section>
      <AnnexBuilder key={room.id} room={room} />
      <details className="inspector-section inspector-card inspector-disclosure room-identity-card">
        <summary>
          <span>
            <h3>Room information</h3>
            <small>Name, code, notes and 3D context</small>
          </span>
          <CaretRight size={16} />
        </summary>
        <div className="property-grid two room-identity-fields">
          <TextField
            label="Room name"
            value={room.name}
            onChange={(name) => updateRoom({ name })}
          />
          <TextField
            label="Room code"
            value={room.code}
            onChange={(code) => updateRoom({ code: code.toUpperCase() })}
          />
        </div>
        <TextField
          label="Description"
          value={room.notes}
          multiline
          onChange={(notes) => updateRoom({ notes })}
        />
        <label className="property-field">
          <span>3D ceiling &amp; services</span>
          <div className="input-with-suffix">
            <select
              value={room.environmentProfileId ?? ""}
              onChange={(event) => updateRoom({ environmentProfileId: event.target.value || null })}
              aria-label="3D ceiling and services profile"
            >
              <option value="">None</option>
              {Object.values(LABORATORY_ENVIRONMENT_PROFILES).map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
          </div>
        </label>
        <div className="room-update-row">
          <span>Last updated: {new Date(room.updatedAt).toLocaleString()}</span>
          <a href={`/digital-twin?room=${encodeURIComponent(room.id)}`}>View in Spatial Index</a>
        </div>
      </details>
      <section className="inspector-section inspector-card surface-library-section">
        <div className="surface-library-header">
          <span>
            <small className="eyebrow">Room finishes</small>
            <h3>Surfaces</h3>
          </span>
          <div className="surface-target-tabs" role="tablist" aria-label="Surface type">
            <button
              role="tab"
              aria-selected={surfaceTarget === "floor"}
              className={surfaceTarget === "floor" ? "active" : ""}
              onClick={() => setSurfaceTarget("floor")}
            >
              Floor
            </button>
            <button
              role="tab"
              aria-selected={surfaceTarget === "walls"}
              className={surfaceTarget === "walls" ? "active" : ""}
              onClick={() => setSurfaceTarget("walls")}
            >
              Walls
            </button>
          </div>
        </div>
        {surfaceTarget === "floor" ? (
          <SurfaceMaterialPicker
            label="Floor finish"
            value={registeredFloorFinish?.id ?? room.floorFinish}
            choices={floorChoices}
            compact
            onChange={(floorFinish) => updateRoom({ floorFinish })}
          />
        ) : (
          <SurfaceMaterialPicker
            label="Wall finish"
            value={registeredWallFinish?.id ?? room.wallFinish}
            choices={wallChoices}
            compact
            onChange={(wallFinish) => updateRoom({ wallFinish })}
          />
        )}
      </section>
    </div>
  );
}

function LayersPanel() {
  const room = useEditorStore(selectActiveRoom);
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const toggleLayer = useEditorStore((state) => state.toggleLayer);
  const updateLayer = useEditorStore((state) => state.updateLayer);
  const updateObject = useEditorStore((state) => state.updateObject);
  const addLayer = useEditorStore((state) => state.addLayer);
  const deleteLayer = useEditorStore((state) => state.deleteLayer);
  const layers = [...room.scene.layers].sort((a, b) => a.order - b.order);
  const move = (index: number, direction: -1 | 1) => {
    const other = layers[index + direction];
    const current = layers[index];
    if (!other) return;
    updateLayer(current.id, { order: other.order });
    updateLayer(other.id, { order: current.order });
  };
  return (
    <div className="inspector-scroll">
      <section className="inspector-section layer-controls">
        <div className="section-heading-row">
          <div>
            <span className="eyebrow">Scene organization</span>
            <h3>{layers.length} layers</h3>
          </div>
          <button className="small-primary" onClick={addLayer}>
            <Plus size={15} />
            Add layer
          </button>
        </div>
        <div className="layer-list">
          {layers.map((layer, index) => {
            const count = room.scene.objects.filter((object) => object.layerId === layer.id).length;
            return (
              <div className={`layer-row ${!layer.visible ? "muted" : ""}`} key={layer.id}>
                <i style={{ background: layer.color }} />
                <input
                  value={layer.name}
                  onChange={(event) => updateLayer(layer.id, { name: event.target.value })}
                  aria-label={`Rename ${layer.name}`}
                />
                <span>{count}</span>
                <button
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  title="Move layer up"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  onClick={() => move(index, 1)}
                  disabled={index === layers.length - 1}
                  title="Move layer down"
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  onClick={() => toggleLayer(layer.id, "locked")}
                  title={layer.locked ? "Unlock layer" : "Lock layer"}
                >
                  {layer.locked ? (
                    <LockSimple size={15} weight="fill" />
                  ) : (
                    <LockSimpleOpen size={15} />
                  )}
                </button>
                <button
                  onClick={() => toggleLayer(layer.id, "visible")}
                  title={layer.visible ? "Hide layer" : "Show layer"}
                >
                  {layer.visible ? <Eye size={16} /> : <EyeSlash size={16} />}
                </button>
                {!layer.system && (
                  <button
                    className="danger-icon"
                    onClick={() => deleteLayer(layer.id)}
                    title="Delete custom layer"
                  >
                    <Trash size={15} />
                  </button>
                )}
                {selectedIds.length > 0 && (
                  <button
                    className="assign-layer"
                    onClick={() =>
                      selectedIds.forEach((id) =>
                        updateObject(id, { layerId: layer.id }, `Assign ${layer.name}`),
                      )
                    }
                  >
                    Assign
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function IndexPanel() {
  return <StorageInspector />;
}

function InventoryPanel() {
  const room = useEditorStore(selectActiveRoom);
  const addInventoryItem = useEditorStore((state) => state.addInventoryItem);
  const updateInventoryItem = useEditorStore((state) => state.updateInventoryItem);
  const removeInventoryItem = useEditorStore((state) => state.removeInventoryItem);
  const setDialog = useEditorStore((state) => state.setDialog);
  const setPanel = useEditorStore((state) => state.setPanel);
  const setSelectedLocation = useEditorStore((state) => state.setSelectedLocation);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<"all" | "assigned" | "unassigned">("all");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [organizer, setOrganizer] = useState<InventoryOrganizerOptions | null>(null);
  const locationById = useMemo(
    () => new Map(room.scene.storageLocations.map((location) => [location.id, location])),
    [room.scene.storageLocations],
  );
  const assignmentLabel = (locationId: string | null) => {
    if (!locationId) return "Unassigned";
    const location = locationById.get(locationId);
    if (!location) return "Unknown location";
    return getLocationPath(room.scene, location.id)
      .map((entry) => entry.name)
      .join(" / ");
  };
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const items = room.scene.inventoryItems.filter((item) => {
    if (scope === "assigned" && !item.storageLocationId) return false;
    if (scope === "unassigned" && item.storageLocationId) return false;
    if (!normalizedSearch) return true;
    return [item.name, item.owner, item.notes, assignmentLabel(item.storageLocationId)]
      .join(" ")
      .toLocaleLowerCase()
      .includes(normalizedSearch);
  });
  const assignedCount = room.scene.inventoryItems.filter((item) => item.storageLocationId).length;

  const createInventoryItem = () => {
    const previousIds = new Set(room.scene.inventoryItems.map((item) => item.id));
    addInventoryItem(null);
    const createdItem = selectActiveRoom(useEditorStore.getState()).scene.inventoryItems.find(
      (item) => !previousIds.has(item.id),
    );
    setSelectedItemId(createdItem?.id ?? null);
    setDialog(null);
  };

  const selectInventoryItem = (itemId: string) => {
    setSelectedItemId(itemId);
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(`[data-inventory-record-id="${itemId}"]`)
        ?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
          block: "start",
        });
    });
  };

  return (
    <div className="inspector-scroll inventory-inspector">
      <section className="inspector-section inventory-overview">
        <div className="section-heading-row">
          <div>
            <span className="eyebrow">Exact room inventory</span>
            <h3>{room.scene.inventoryItems.length} indexed items</h3>
          </div>
          <button className="small-primary" onClick={createInventoryItem}>
            <Plus size={15} />
            Add item
          </button>
        </div>
        <div className="inventory-metrics" aria-label="Inventory assignment status">
          <Stat label="Assigned" value={assignedCount} />
          <Stat label="Unassigned" value={room.scene.inventoryItems.length - assignedCount} />
          <Stat label="Locations" value={room.scene.storageLocations.length} />
        </div>
        <label className="search-field inventory-search">
          <MagnifyingGlass size={17} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search items, owners, or locations…"
            aria-label="Search room inventory"
          />
        </label>
        <div className="filter-chips inventory-filter-chips">
          {(["all", "assigned", "unassigned"] as const).map((value) => (
            <button
              key={value}
              className={scope === value ? "active" : ""}
              onClick={() => setScope(value)}
            >
              {value[0].toUpperCase() + value.slice(1)}
            </button>
          ))}
        </div>
      </section>
      <section className="inspector-section inventory-editor-list" aria-label="Inventory items">
        {items.map((item) => (
          <article
            className={`inventory-editor-card ${selectedItemId === item.id ? "active" : ""}`}
            key={item.id}
            data-inventory-record-id={item.id}
            onClick={() => selectInventoryItem(item.id)}
            onPointerDown={() => {
              if (selectedItemId !== item.id) selectInventoryItem(item.id);
            }}
            onFocusCapture={() => {
              if (selectedItemId !== item.id) selectInventoryItem(item.id);
            }}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                selectInventoryItem(item.id);
              }
            }}
            tabIndex={0}
          >
            <div className="inventory-editor-card-heading">
              <span className="inventory-record-icon">
                <Package size={18} weight="duotone" />
              </span>
              <label>
                <span>Item name</span>
                <input
                  value={item.name}
                  onClick={() => selectInventoryItem(item.id)}
                  onFocus={() => selectInventoryItem(item.id)}
                  onChange={(event) => updateInventoryItem(item.id, { name: event.target.value })}
                />
              </label>
              <button
                className="danger-icon"
                title={`Delete ${item.name}`}
                aria-label={`Delete ${item.name}`}
                onClick={() => {
                  if (window.confirm(`Delete ${item.name} from this room's inventory?`)) {
                    removeInventoryItem(item.id);
                    setSelectedItemId((selectedId) => (selectedId === item.id ? null : selectedId));
                  }
                }}
              >
                <Trash size={15} />
              </button>
            </div>
            <div className="inventory-editor-fields">
              <label>
                <span>Quantity</span>
                <input
                  type="number"
                  min="0"
                  value={item.quantity}
                  onChange={(event) =>
                    updateInventoryItem(item.id, { quantity: Number(event.target.value) })
                  }
                />
              </label>
              <label>
                <span>Unit</span>
                <input
                  value={item.unit}
                  onChange={(event) => updateInventoryItem(item.id, { unit: event.target.value })}
                />
              </label>
              <label className="inventory-owner-field">
                <span>Owner</span>
                <input
                  value={item.owner}
                  placeholder="Shared"
                  onChange={(event) => updateInventoryItem(item.id, { owner: event.target.value })}
                />
              </label>
            </div>
            <div className="inventory-location-field">
              <span>Exact storage location</span>
              <button
                className="inventory-choose-location"
                onClick={() =>
                  setOrganizer({
                    initialItems: [{ roomId: room.id, itemId: item.id }],
                    initialRoomId: room.id,
                    initialLocationId: item.storageLocationId,
                  })
                }
              >
                <MapPin size={17} />
                <span>
                  {assignmentLabel(item.storageLocationId)}
                  <small>Choose or change location</small>
                </span>
                <CaretRight size={17} />
              </button>
            </div>
            <div className="inventory-assignment-evidence">
              <span className={item.storageLocationId ? "assigned" : "unassigned"}>
                {item.storageLocationId ? "Assigned" : "Needs location"}
              </span>
              {item.storageLocationId && (
                <button
                  onClick={() => {
                    setSelectedLocation(item.storageLocationId);
                    setPanel("index");
                  }}
                >
                  Show storage path
                </button>
              )}
            </div>
          </article>
        ))}
        {!items.length && (
          <div className="empty-state compact inventory-empty-state">
            <Package size={25} weight="duotone" />
            <b>No matching inventory</b>
            <span>Adjust the filter or add an item for this room.</span>
          </div>
        )}
      </section>
      {organizer && <InventoryOrganizer {...organizer} onClose={() => setOrganizer(null)} />}
    </div>
  );
}

function ObjectProperties({ object }: { object: SceneObject }) {
  const room = useEditorStore(selectActiveRoom);
  const updateObject = useEditorStore((state) => state.updateObject);
  const duplicate = useEditorStore((state) => state.duplicateSelected);
  const remove = useEditorStore((state) => state.deleteSelected);
  const setSelectedLocation = useEditorStore((state) => state.setSelectedLocation);
  const initializeStorageForObject = useEditorStore((state) => state.initializeStorageForObject);
  const addChild = useEditorStore((state) => state.addStorageChild);
  const definition = getAssetDefinition(object.assetDefinitionId);
  const root = room.scene.storageLocations.find(
    (location) => location.objectId === object.id && !location.parentId,
  );
  const descendants = room.scene.storageLocations.filter(
    (location) => location.objectId === object.id && location.id !== root?.id,
  );
  const record = room.scene.equipmentRecords.find((entry) => entry.objectId === object.id);
  const updateRecord = useEditorStore((state) => state.updateEquipmentRecord);
  const maximumZ = Math.max(0, ...room.scene.objects.map((entry) => entry.zIndex));
  const minimumZ = Math.min(0, ...room.scene.objects.map((entry) => entry.zIndex));
  const isHostedOpening = Boolean(object.opening);
  const hostedWall = object.opening
    ? room.scene.objects.find((entry) => entry.id === object.opening?.wallId && entry.wall)
    : undefined;
  const supportsFreeTransform = !["wall", "door", "window"].includes(object.objectType);
  const objectWallFinish = object.wall
    ? wallFinishForObject(object.metadata, room.wallFinish)
    : null;
  const setNumber = (group: "position" | "dimensions" | "rotation", key: string, value: string) => {
    const current = object[group] as Record<string, number>;
    updateObject(object.id, { [group]: { ...current, [key]: Number(value) } }, `Edit ${key}`);
  };
  return (
    <div className="inspector-scroll inspector-studio-panel object-properties-panel">
      <section className="inspector-section selected-summary property-hero inspector-dossier">
        <div className="selected-preview">
          <AssetThumbnail asset={definition} />
        </div>
        <div>
          <span className="eyebrow">Selected {object.objectType}</span>
          <h3>{object.name}</h3>
          <code>{object.indexCode}</code>
          <span className="property-hero-meta">
            <em>{definition.category}</em>
            <em>
              {Math.round(object.dimensions.width)} × {Math.round(object.dimensions.depth)} ×{" "}
              {Math.round(object.dimensions.height)} mm
            </em>
          </span>
        </div>
      </section>
      <section className="inspector-section property-card identity-property-card">
        <div className="property-section-title">
          <span className="eyebrow">Identity</span>
          <h3>Object record</h3>
        </div>
        <TextField
          label="Name"
          value={object.name}
          onChange={(name) => updateObject(object.id, { name })}
        />
        <details className="property-disclosure" key={`identity-${object.id}`}>
          <summary>Classification & codes</summary>
          <TextField
            label="Index code"
            value={object.indexCode}
            onChange={(indexCode) =>
              updateObject(object.id, { indexCode: indexCode.toUpperCase() })
            }
          />
          <label className="property-field">
            <span>Layer</span>
            <select
              value={object.layerId}
              onChange={(event) => updateObject(object.id, { layerId: event.target.value })}
            >
              {room.scene.layers.map((layer) => (
                <option key={layer.id} value={layer.id}>
                  {layer.name}
                </option>
              ))}
            </select>
          </label>
          <label className="property-field">
            <span>Zone</span>
            <select
              value={object.zoneId ?? ""}
              onChange={(event) => updateObject(object.id, { zoneId: event.target.value || null })}
            >
              <option value="">None</option>
              {room.scene.zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.code} · {zone.name}
                </option>
              ))}
            </select>
          </label>
        </details>
      </section>
      <section className="inspector-section property-card transform-property-card">
        <div className="property-section-title">
          <span className="eyebrow">Geometry</span>
          <h3>Position and dimensions</h3>
        </div>
        {!isHostedOpening && (
          <div className="property-coordinate-group">
            <div className="property-subsection-label">
              <span>Coordinates</span>
              <small>Room origin · millimetres</small>
            </div>
            <div className={`property-grid ${supportsFreeTransform ? "three" : "two"}`}>
              <TextField
                label="X"
                value={Math.round(object.position.x)}
                type="number"
                suffix="mm"
                onChange={(value) => setNumber("position", "x", value)}
              />
              <TextField
                label="Y"
                value={Math.round(object.position.y)}
                type="number"
                suffix="mm"
                onChange={(value) => setNumber("position", "y", value)}
              />
              {supportsFreeTransform && (
                <TextField
                  label="Elevation"
                  value={Math.round(object.position.z)}
                  type="number"
                  suffix="mm"
                  onChange={(value) =>
                    updateObject(
                      object.id,
                      {
                        position: {
                          ...object.position,
                          z: normalizeRaisedFromFloorMm(value, object.position.z),
                        },
                      },
                      "Set raised from floor",
                    )
                  }
                />
              )}
            </div>
          </div>
        )}
        <div className="property-coordinate-group">
          <div className="property-subsection-label">
            <span>Physical size</span>
            <small>Authored envelope</small>
          </div>
          <div className={`property-grid ${isHostedOpening ? "two" : "three"}`}>
            <TextField
              label="Width"
              value={Math.round(object.dimensions.width)}
              type="number"
              suffix="mm"
              onChange={(value) => setNumber("dimensions", "width", value)}
            />
            {!isHostedOpening && (
              <TextField
                label="Depth"
                value={Math.round(object.dimensions.depth)}
                type="number"
                suffix="mm"
                onChange={(value) => setNumber("dimensions", "depth", value)}
              />
            )}
            <TextField
              label="Height"
              value={Math.round(object.dimensions.height)}
              type="number"
              suffix="mm"
              onChange={(value) => setNumber("dimensions", "height", value)}
            />
          </div>
        </div>
        {!isHostedOpening && (
          <TextField
            label="Rotation"
            value={Math.round(object.rotation.z)}
            type="number"
            suffix="°"
            onChange={(value) => setNumber("rotation", "z", value)}
          />
        )}
        {supportsFreeTransform && (
          <div className="toggle-row transform-flip-row" aria-label="Object mirroring">
            <button
              className={object.flipHorizontal ? "active" : ""}
              aria-pressed={object.flipHorizontal}
              title="Mirror this object from left to right"
              onClick={() =>
                updateObject(
                  object.id,
                  { flipHorizontal: !object.flipHorizontal },
                  "Flip object horizontally",
                )
              }
            >
              Flip horizontal
            </button>
            <button
              className={object.flipVertical ? "active" : ""}
              aria-pressed={object.flipVertical}
              title="Mirror this object from front to back"
              onClick={() =>
                updateObject(
                  object.id,
                  { flipVertical: !object.flipVertical },
                  "Flip object vertically",
                )
              }
            >
              Flip vertical
            </button>
          </div>
        )}
        <div className="toggle-row property-state-bar">
          <span>Locked</span>
          <button
            className={object.locked ? "active" : ""}
            onClick={() => updateObject(object.id, { locked: !object.locked })}
          >
            {object.locked ? <LockSimple size={15} /> : <LockSimpleOpen size={15} />}
            {object.locked ? "Locked" : "Unlocked"}
          </button>
          <span>Visible</span>
          <button
            className={object.visible ? "active" : ""}
            onClick={() => updateObject(object.id, { visible: !object.visible })}
          >
            {object.visible ? <Eye size={15} /> : <EyeSlash size={15} />}
            {object.visible ? "Shown" : "Hidden"}
          </button>
        </div>
        <details className="property-notes-block property-disclosure" key={`notes-${object.id}`}>
          <summary>Notes</summary>
          <TextField
            label="Notes"
            value={String(object.metadata.notes ?? "")}
            multiline
            onChange={(notes) =>
              updateObject(object.id, { metadata: { ...object.metadata, notes } })
            }
          />
        </details>
      </section>
      {object.wall && (
        <section className="inspector-section surface-library-section">
          <div className="section-heading-row">
            <div>
              <span className="eyebrow">Selected wall</span>
              <h3>Wall material</h3>
            </div>
            {typeof object.metadata.wallFinishId === "string" && (
              <button
                className="small-primary"
                onClick={() => {
                  const metadata = { ...object.metadata };
                  delete metadata.wallFinishId;
                  updateObject(object.id, { metadata }, "Use room wall material");
                }}
              >
                Use room finish
              </button>
            )}
          </div>
          <SurfaceMaterialPicker
            label="Finish"
            value={objectWallFinish!.id}
            compact
            choices={LABORATORY_WALL_FINISHES.map((finish) => ({
              id: finish.id,
              label: finish.label,
              description: finish.description,
              color: finish.color,
              accent: finish.baseboardColor,
              textureKind: finish.textureKind,
            }))}
            onChange={(wallFinishId) =>
              updateObject(
                object.id,
                { metadata: { ...object.metadata, wallFinishId } },
                "Change wall material",
              )
            }
          />
        </section>
      )}
      {object.wall && (
        <section className="inspector-section">
          <h3>Wall geometry</h3>
          <div className="property-grid two">
            <TextField
              label="Start X"
              value={Math.round(object.wall.start.x)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  { wall: { ...object.wall!, start: { ...object.wall!.start, x: Number(value) } } },
                  "Edit wall endpoint",
                )
              }
            />
            <TextField
              label="Start Y"
              value={Math.round(object.wall.start.y)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  { wall: { ...object.wall!, start: { ...object.wall!.start, y: Number(value) } } },
                  "Edit wall endpoint",
                )
              }
            />
          </div>
          <div className="property-grid two">
            <TextField
              label="End X"
              value={Math.round(object.wall.end.x)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  { wall: { ...object.wall!, end: { ...object.wall!.end, x: Number(value) } } },
                  "Edit wall endpoint",
                )
              }
            />
            <TextField
              label="End Y"
              value={Math.round(object.wall.end.y)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  { wall: { ...object.wall!, end: { ...object.wall!.end, y: Number(value) } } },
                  "Edit wall endpoint",
                )
              }
            />
          </div>
          <div className="property-grid two">
            <TextField
              label="Thickness"
              value={Math.round(object.wall.thickness)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  { wall: { ...object.wall!, thickness: Number(value) } },
                  "Edit wall thickness",
                )
              }
            />
            <TextField
              label="Wall height"
              value={Math.round(object.wall.height)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  { wall: { ...object.wall!, height: Number(value) } },
                  "Edit wall height",
                )
              }
            />
          </div>
        </section>
      )}
      {object.opening && (
        <section className="inspector-section">
          <h3>{object.objectType === "door" ? "Door opening" : "Window opening"}</h3>
          <div className="info-stat">
            <span>Hosted on</span>
            <b>{hostedWall?.name ?? "Missing wall"}</b>
          </div>
          <div className="property-grid two">
            <TextField
              label="Wall offset"
              value={Math.round(object.opening.offset)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  { opening: { ...object.opening!, offset: Number(value) } },
                  "Move wall opening",
                )
              }
            />
            <TextField
              label="Raised from floor"
              value={Math.round(object.opening.sillHeight)}
              type="number"
              suffix="mm"
              onChange={(value) =>
                updateObject(
                  object.id,
                  {
                    opening: {
                      ...object.opening!,
                      sillHeight: normalizeRaisedFromFloorMm(value, object.opening!.sillHeight),
                    },
                  },
                  "Set opening height from floor",
                )
              }
            />
          </div>
          <div className="toggle-row transform-flip-row" aria-label="Opening direction">
            <button
              className={
                object.objectType === "door"
                  ? object.opening.handing === "right"
                    ? "active"
                    : ""
                  : object.flipHorizontal
                    ? "active"
                    : ""
              }
              aria-pressed={
                object.objectType === "door"
                  ? object.opening.handing === "right"
                  : object.flipHorizontal
              }
              onClick={() =>
                object.objectType === "door"
                  ? updateObject(
                      object.id,
                      {
                        opening: {
                          ...object.opening!,
                          handing: object.opening!.handing === "left" ? "right" : "left",
                        },
                      },
                      "Flip door horizontally",
                    )
                  : updateObject(
                      object.id,
                      { flipHorizontal: !object.flipHorizontal },
                      "Flip window horizontally",
                    )
              }
            >
              Flip horizontal
            </button>
            <button
              className={
                object.objectType === "door"
                  ? object.opening.swing === "outward"
                    ? "active"
                    : ""
                  : object.flipVertical
                    ? "active"
                    : ""
              }
              aria-pressed={
                object.objectType === "door"
                  ? object.opening.swing === "outward"
                  : object.flipVertical
              }
              disabled={object.objectType === "door" && object.opening.swing === "sliding"}
              title={
                object.objectType === "door" && object.opening.swing === "sliding"
                  ? "Sliding doors do not have an inward or outward swing"
                  : "Mirror the opening from front to back"
              }
              onClick={() =>
                object.objectType === "door"
                  ? updateObject(
                      object.id,
                      {
                        opening: {
                          ...object.opening!,
                          swing: object.opening!.swing === "inward" ? "outward" : "inward",
                        },
                      },
                      "Flip door vertically",
                    )
                  : updateObject(
                      object.id,
                      { flipVertical: !object.flipVertical },
                      "Flip window vertically",
                    )
              }
            >
              Flip vertical
            </button>
          </div>
          {object.objectType === "door" && (
            <div className="property-grid two">
              <label className="property-field">
                <span>Handing</span>
                <select
                  value={object.opening.handing}
                  onChange={(event) =>
                    updateObject(
                      object.id,
                      {
                        opening: {
                          ...object.opening!,
                          handing: event.target.value as "left" | "right",
                        },
                      },
                      "Edit door handing",
                    )
                  }
                >
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <label className="property-field">
                <span>Swing</span>
                <select
                  value={object.opening.swing}
                  onChange={(event) =>
                    updateObject(
                      object.id,
                      {
                        opening: {
                          ...object.opening!,
                          swing: event.target.value as "inward" | "outward" | "sliding",
                        },
                      },
                      "Edit door swing",
                    )
                  }
                >
                  <option value="inward">Inward</option>
                  <option value="outward">Outward</option>
                  <option value="sliding">Sliding</option>
                </select>
              </label>
            </div>
          )}
        </section>
      )}
      {definition.indexingBehavior === "storage" &&
        missingStorageCount(definition, object.id, room.scene.storageLocations) > 0 && (
          <section className="inspector-section storage-config">
            <div className="section-heading-row">
              <div>
                <span className="eyebrow">Storage configuration</span>
                <h3>{root ? "Complete storage setup" : "Authored storage available"}</h3>
              </div>
            </div>
            <p className="muted-copy">
              Add the authored drawers, cabinets, and shelves for this exact bench or storage family
              so every location can be indexed in the Spatial Index. Existing records and inventory
              assignments are kept. This action can be undone.
            </p>
            <button onClick={() => initializeStorageForObject(object.id)}>
              <TreeStructure size={16} />
              {root ? "Complete missing storage" : "Set up storage locations"}
            </button>
          </section>
        )}
      {root && (
        <section className="inspector-section storage-config">
          <div className="section-heading-row">
            <div>
              <span className="eyebrow">Storage configuration</span>
              <h3>{descendants.length} internal locations</h3>
            </div>
            <button
              onClick={() => {
                setSelectedLocation(root.id);
              }}
            >
              <TreeStructure size={16} />
              Open index
            </button>
          </div>
          <div className="stat-grid">
            <Stat
              label="Shelves"
              value={descendants.filter((location) => location.type === "shelf").length}
            />
            <Stat
              label="Drawers"
              value={descendants.filter((location) => location.type === "drawer").length}
            />
            <Stat
              label="Compartments"
              value={descendants.filter((location) => location.type === "compartment").length}
            />
            <Stat
              label="Bins"
              value={descendants.filter((location) => location.type === "bin").length}
            />
          </div>
          <div className="add-child-row">
            <button onClick={() => addChild(root.id, "shelf")}>
              <Plus size={13} />
              Shelf
            </button>
            <button onClick={() => addChild(root.id, "drawer")}>
              <Plus size={13} />
              Drawer
            </button>
            <button onClick={() => addChild(root.id, "compartment")}>
              <Plus size={13} />
              Compartment
            </button>
          </div>
        </section>
      )}
      {record && (
        <EquipmentEditor record={record} update={(patch) => updateRecord(record.id, patch)} />
      )}
      <section className="inspector-section action-footer">
        <button onClick={() => updateObject(object.id, { zIndex: maximumZ + 1 }, "Bring forward")}>
          <ArrowUp size={16} />
          Bring forward
        </button>
        <button onClick={() => updateObject(object.id, { zIndex: minimumZ - 1 }, "Send backward")}>
          <ArrowDown size={16} />
          Send backward
        </button>
        <button
          onClick={duplicate}
          title="Duplicate selected item (Shift+D; Ctrl+D / ⌘D also supported)"
          aria-keyshortcuts="Shift+d Control+d Meta+d"
        >
          <Selection size={16} />
          Duplicate
        </button>
        <button className="danger" onClick={remove}>
          <Trash size={16} />
          Delete
        </button>
      </section>
    </div>
  );
}

function EquipmentEditor({
  record,
  update,
}: {
  record: EquipmentRecord;
  update: (patch: Partial<EquipmentRecord>) => void;
}) {
  return (
    <section className="inspector-section equipment-record">
      <div className="section-heading-row">
        <div>
          <span className="eyebrow">Equipment record</span>
          <h3>{record.equipmentId}</h3>
        </div>
        <span className={`status-badge ${record.status}`}>
          {record.status.replaceAll("-", " ")}
        </span>
      </div>
      <div className="equipment-record-photo-row">
        {record.imageSrc ? (
          <img src={record.imageSrc} alt="" className="equipment-record-photo" />
        ) : (
          <span className="equipment-record-photo equipment-record-photo-empty">
            <Package size={24} weight="duotone" />
          </span>
        )}
        <TextField
          label="Record photo URL"
          value={record.imageSrc ?? ""}
          type="url"
          onChange={(imageSrc) => update({ imageSrc: imageSrc || undefined })}
        />
      </div>
      <div className="property-grid two">
        <TextField
          label="Equipment ID"
          value={record.equipmentId}
          onChange={(equipmentId) => update({ equipmentId })}
        />
        <TextField
          label="Manufacturer"
          value={record.manufacturer}
          onChange={(manufacturer) => update({ manufacturer })}
        />
      </div>
      <div className="property-grid two">
        <TextField label="Model" value={record.model} onChange={(model) => update({ model })} />
        <TextField
          label="Serial number"
          value={record.serialNumber}
          onChange={(serialNumber) => update({ serialNumber })}
        />
      </div>
      <label className="property-field">
        <span>Status</span>
        <select
          value={record.status}
          onChange={(event) => update({ status: event.target.value as EquipmentRecord["status"] })}
        >
          <option value="active">Active</option>
          <option value="service-due">Service due</option>
          <option value="out-of-service">Out of service</option>
          <option value="reserved">Reserved</option>
        </select>
      </label>
      <TextField
        label="Responsible person"
        value={record.responsiblePerson}
        onChange={(responsiblePerson) => update({ responsiblePerson })}
      />
      <div className="property-grid two">
        <TextField
          label="Last service"
          value={record.lastServiceDate ?? ""}
          type="date"
          onChange={(lastServiceDate) => update({ lastServiceDate: lastServiceDate || null })}
        />
        <TextField
          label="Next service"
          value={record.nextServiceDate ?? ""}
          type="date"
          onChange={(nextServiceDate) => update({ nextServiceDate: nextServiceDate || null })}
        />
      </div>
      <div className="property-grid two">
        <TextField
          label="Power"
          value={record.powerRequirements}
          onChange={(powerRequirements) => update({ powerRequirements })}
        />
        <TextField
          label="Water"
          value={record.waterRequirements}
          onChange={(waterRequirements) => update({ waterRequirements })}
        />
      </div>
      <div className="property-grid two">
        <TextField
          label="Gas"
          value={record.gasRequirements}
          onChange={(gasRequirements) => update({ gasRequirements })}
        />
        <TextField label="Notes" value={record.notes} onChange={(notes) => update({ notes })} />
      </div>
      <div className="toggle-row">
        <span>Drain</span>
        <button
          className={record.drainRequired ? "active" : ""}
          onClick={() => update({ drainRequired: !record.drainRequired })}
        >
          {record.drainRequired ? "Required" : "Not required"}
        </button>
        <span>Ventilation</span>
        <button
          className={record.ventilationRequired ? "active" : ""}
          onClick={() => update({ ventilationRequired: !record.ventilationRequired })}
        >
          {record.ventilationRequired ? "Required" : "Not required"}
        </button>
      </div>
    </section>
  );
}

function PropertiesPanel() {
  const room = useEditorStore(selectActiveRoom);
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const selected = useMemo(
    () => room.scene.objects.filter((object) => selectedIds.includes(object.id)),
    [room.scene.objects, selectedIds],
  );
  if (!selected.length)
    return (
      <div className="empty-state panel-empty">
        <Selection size={38} />
        <b>Select an object</b>
        <span>
          Its dimensions, index identity, storage configuration, and equipment record will appear
          here.
        </span>
      </div>
    );
  if (selected.length > 1)
    return (
      <div className="empty-state panel-empty">
        <Selection size={38} />
        <b>{selected.length} objects selected</b>
        <span>Drag the group, duplicate it, assign a layer, or delete the selection.</span>
      </div>
    );
  return <ObjectProperties object={selected[0]} />;
}

function ValidationPanel() {
  const room = useEditorStore(selectActiveRoom);
  const setSelected = useEditorStore((state) => state.setSelected);
  const setPresentation = useEditorStore((state) => state.setPresentation);
  const warnings = validatePlacement(room);
  return (
    <div className="inspector-scroll validation-panel">
      <section className="inspector-section">
        <div className="section-heading-row">
          <div>
            <span className="eyebrow">Non-destructive checks</span>
            <h3>{warnings.length} placement warnings</h3>
          </div>
          <span className={`validation-score ${warnings.length ? "has-warnings" : "clear"}`}>
            {warnings.length ? "Review" : "Clear"}
          </span>
        </div>
        <p className="section-copy">Warnings help review a layout but do not block placement.</p>
        <div className="warning-list">
          {warnings.map((warning) => (
            <button
              key={warning.id}
              onClick={() => {
                setSelected(warning.objectIds);
                setPresentation("split");
              }}
            >
              <Warning size={18} weight={warning.severity === "error" ? "fill" : "regular"} />
              <span>
                <b>{warning.title}</b>
                {warning.message}
              </span>
              <em>{warning.objectIds.length}</em>
            </button>
          ))}
          {!warnings.length && (
            <div className="empty-state compact">
              <Selection size={24} />
              <b>No current warnings</b>
              <span>Objects are inside the room and index codes are unique.</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

type InspectorPanelsProps = {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
};

export function InspectorPanels({ collapsed = false, onCollapsedChange }: InspectorPanelsProps) {
  const panel = useEditorStore((state) => state.panel);
  const setPanel = useEditorStore((state) => state.setPanel);
  const warningCount = validatePlacement(useEditorStore(selectActiveRoom)).length;

  if (collapsed) {
    return (
      <section
        id="room-inspector-panel"
        className="inspector is-collapsed"
        aria-label="Room inspector"
      >
        <button
          className="collapsed-panel-rail inspector-rail"
          onClick={() => onCollapsedChange?.(false)}
          aria-label="Expand room inspector"
          aria-expanded="false"
          aria-controls="room-inspector-panel"
          title="Expand room inspector"
        >
          <CaretLeft size={18} />
          <span>Inspector</span>
        </button>
      </section>
    );
  }

  return (
    <section id="room-inspector-panel" className="inspector" aria-label="Room inspector">
      <div className="inspector-tab-row">
        <div className="inspector-tabs" role="tablist">
          {panelTabs
            .filter((tab) => !["index", "inventory"].includes(tab.id))
            .map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={panel === tab.id}
                  className={panel === tab.id ? "active" : ""}
                  onClick={() => setPanel(tab.id)}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                  {tab.id === "validation" && warningCount > 0 && <em>{warningCount}</em>}
                </button>
              );
            })}
        </div>
        <button
          className="inspector-collapse-control"
          onClick={() => onCollapsedChange?.(true)}
          aria-label="Collapse room inspector"
          aria-expanded="true"
          aria-controls="room-inspector-panel"
          title="Collapse room inspector"
        >
          <CaretRight size={17} />
        </button>
      </div>
      <div className="inspector-workspace-links" role="tablist" aria-label="Storage and inventory">
        {panelTabs
          .filter((tab) => ["index", "inventory"].includes(tab.id))
          .map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-label={tab.label}
              aria-selected={panel === tab.id}
              onClick={() => setPanel(tab.id)}
            >
              <tab.icon size={16} />
              <span>
                <b>{tab.label}</b>
                <small>{tab.id === "index" ? "Locations & names" : "Room items"}</small>
              </span>
              <CaretRight size={13} />
            </button>
          ))}
      </div>
      <div className="inspector-content" role="tabpanel">
        {panel === "room" && <RoomPanel />}
        {panel === "layers" && <LayersPanel />}
        {panel === "index" && <IndexPanel />}
        {panel === "inventory" && <InventoryPanel />}
        {panel === "properties" && <PropertiesPanel />}
        {panel === "validation" && <ValidationPanel />}
      </div>
    </section>
  );
}
